interface ErrorResponse {
  error_code?: string;
  [key: string]: unknown;
}

/** Content security error types returned by the backend */
export const CONTENT_SECURITY_ERROR = {
  PROMPT_INJECTION: 'PromptInjectionException',
  DEFAULT_MESSAGE:
    "This input doesn't seem to fit the intended use of this feature. Please revise it and try again.",
} as const;

const USER_REF_BAN_CONTACT_EMAIL = 'support@heidihealth.com';

export const USER_REF_BAN_ERROR = {
  CODE: 'user_ref_banned',
  CONTACT_EMAIL: USER_REF_BAN_CONTACT_EMAIL,
  CONTACT_URL: `mailto:${USER_REF_BAN_CONTACT_EMAIL}`,
} as const;

/** Error codes returned by getErrorMessage for timeline stream (used for equality checks). */
export const TIMELINE_STREAM_ERROR = {
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  PROMPT_INJECTION: 'PROMPT_INJECTION',
  USER_REF_BANNED: 'USER_REF_BANNED',
  /** Network / connectivity failure with no usable HTTP status (REG-51 / SWR-35). */
  QUERY_NETWORK: 'QUERY_NETWORK',
  /** Server / generic query failure (REG-51 / SWR-35). */
  QUERY_FAILED: 'QUERY_FAILED',
  /** Soft / idle / hard stream timeout (REG-51 / SWR-35). */
  QUERY_TIMEOUT: 'QUERY_TIMEOUT',
} as const;

/**
 * Map timeline `error_item` content (sentinels or legacy strings) to user-facing copy.
 * Callers pass `intl.formatMessage` (react-intl) — do not add keys to common.json.
 */
export const resolveTimelineErrorDisplay = (
  content: string,
  formatMessage: (descriptor: {
    id?: string;
    defaultMessage: string;
    description?: string;
  }) => string
): string => {
  switch (content) {
    case TIMELINE_STREAM_ERROR.QUERY_NETWORK:
      return formatMessage({
        defaultMessage:
          "We couldn't complete that query. This may be a network issue — please check your connection and try again.",
        id: 'aHlt3G9x1P',
        description: 'REG-51 / SWR-35 Evidence query network failure message',
      });
    case TIMELINE_STREAM_ERROR.QUERY_FAILED:
      return formatMessage({
        defaultMessage: "We couldn't complete that query. Please try again.",
        id: 'YR4ZiYrihi',
        description: 'REG-51 / SWR-35 Evidence query server/generic failure message',
      });
    case TIMELINE_STREAM_ERROR.QUERY_TIMEOUT:
      return formatMessage({
        defaultMessage: 'This is taking longer than expected. Please try again.',
        id: 'uiBrF5hJIk',
        description: 'REG-51 / SWR-35 Evidence query timeout message',
      });
    case TIMELINE_STREAM_ERROR.PROMPT_INJECTION:
      return CONTENT_SECURITY_ERROR.DEFAULT_MESSAGE;
    default:
      return content;
  }
};

export const isUserRefBannedErrorResponse = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error_code' in data &&
    (data as ErrorResponse).error_code === USER_REF_BAN_ERROR.CODE
  );
};
